import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import isEmpty from "lodash/isEmpty";
import React, { FC, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useGetSessionsAndEventsList } from "../../../../api/services/program/programApis";
import {
  AccordionHeader,
  AccordionHeaderText,
  ChipOptionsDataType,
  MuiBox,
  MuiButtonTertiary,
  MuiDoubleLabel,
  MuiFooterButtons,
  MuiGrid,
} from "../../../../components";
import { MuiDoubleLabelWithChip } from "../../../../components/doubleLabelWithChip/MuiDoubleLabelWithChip";
import { ExpandCollapseButton } from "../../../../components/expandCollapseButton/ExpandCollapseButton";
import PermissionWrapper from "../../../../components/permissionWrapper/PermissionWrapper";
import { MODULE_PERMISSIONS, PAGE_IDS } from "../../../../helpers/constants";
import useAccordion from "../../../../hooks/useAccordion";
import { useNavigationSiteCore } from "../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { useToast } from "../../../../hooks/useToast";
import { siteCoreContentLoadingSelector } from "../../../../redux/slices/appState/appStateSelector";
import {
  toggleFullSidebar,
  toggleFullSidebarWithCollapseButton,
  toggleLoader,
} from "../../../../redux/slices/appState/appStateSlice";
import { ManageParticipantsSessionAndEventsTab } from "../../../coachPortal/components/manageParticipantsSessionAndEventsTab/ManageParticipantsSessionAndEventsTab";
import {
  CATEGORY_FILTER_KEYS,
  ManageParticipantsSessionAndEventsFilterFormKeys,
  TYPE_FILTER_KEYS,
  UPCOMING_PERIOD_FILTER_KEYS,
} from "../../../coachPortal/modelsAndSchemas/Models";
import ParticipantActivityTimeline from "../../../createProgram/components/createProgramTabs/tabs/participant/components/participantActivityTimeline/ParticipantActivityTimeline";
import { createGenericAccounts } from "../../../genericAccounts/createGenericAccount/siteCore/SiteCoreKeys";
import ViewGenericAccountHeader from "../../../genericAccounts/viewGenericAccount/viewGenericAccountHeader/ViewGenericAccountHeader";
import { ParticipantDetails } from "../modelsAndSchemas/Models";
import { EditViewParticipantWrapper } from "./EditViewParticipantComponent.style";
import { CPParticipantActivityTimelineWrapper } from "../../../coachPortal/components/cpParticipantActivityTimeline/CPParticipantActivityTimeline.style";

export interface EditViewParticipantComponentProps {
  participantData: ParticipantDetails | undefined;
  headerTitle: string;
  primaryButtonTitle: string;
  secondaryButtonTitle: string;
  showEditButtonInHeader: boolean;
  onHeaderEditButtonClick?: () => void;
  onPrimaryButtonClick: () => void;
  onSecondaryButtonClick?: () => void;
  participantId?: string;
  isEditProgram?: boolean;
}

const initialAccordionStatus = {
  all: true,
  participantInformation: true,
  programInformation: true,
  platformDetails: true,
  contactPreferences: true,
  upcomingMeetings: true,
  participantActivityTimeline: true,
};

export const EditViewParticipantComponent: FC<
  EditViewParticipantComponentProps
> = ({
  participantData,
  headerTitle,
  onHeaderEditButtonClick,
  onPrimaryButtonClick,
  onSecondaryButtonClick,
  showEditButtonInHeader,
  primaryButtonTitle,
  secondaryButtonTitle,
  participantId = "",
  isEditProgram = false,
}) => {
  const gridSize = { md: 4, sm: 6, xs: 12 };
  const dispatch = useDispatch();
  const { commonLabels, fetchComponentTitle, fetchLabel } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const navigate = useNavigate();
  const { displayToast } = useToast();
  const siteCoreLoading = useSelector(siteCoreContentLoadingSelector);
  const [sectionHeaderTitles, setSectionHeaderTitles] = useState<
    Record<string, string>
  >({});
  const {
    accordionStatus,
    handleIndividualAccordionChange,
    handleExpandCollapseAllChange,
  } = useAccordion(initialAccordionStatus);

  const {
    data: sessionsEventList,
    isFetching: isSessionsEventsPending,
    isError: isSessionsEventsError,
    refetchWithParams: getSessionsAndEventsList,
  } = useGetSessionsAndEventsList(true);

  useEffect(() => {
    if (participantId) {
      getSessionsAndEventsList(participantId);
    }
  }, [participantId]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        sessionEvents: {
          isVisible: isSessionsEventsPending,
        },
      }),
    );
    return () => {
      dispatch(toggleLoader({ sessionEvents: { isVisible: false } }));
    };
  }, [isSessionsEventsPending]);

  useEffect(() => {
    if (isSessionsEventsError) {
      displayToast(commonLabels.someThingWentWrongPleaseTryAgainLater, "error");
      dispatch(toggleLoader({ sessionEvents: { isVisible: false } }));
    }
  }, [isSessionsEventsError]);

  const sessionsAndEventsFormDefaultValues = {
    [ManageParticipantsSessionAndEventsFilterFormKeys.type]:
      TYPE_FILTER_KEYS.UPCOMING_SESSIONS_AND_EVENTS,
    [ManageParticipantsSessionAndEventsFilterFormKeys.period]:
      UPCOMING_PERIOD_FILTER_KEYS.NEXT_7_DAYS,
    [ManageParticipantsSessionAndEventsFilterFormKeys.category]:
      CATEGORY_FILTER_KEYS.LIVE_WEBINAR_EVENTS,
  };

  const upcomingEventMethods = useForm({
    mode: "all",
    defaultValues: sessionsAndEventsFormDefaultValues,
  });

  const participantActivityTimelineMethods = useForm({
    mode: "all",
  });

  const handleSecondaryButtonClick = () => {
    if (onSecondaryButtonClick) onSecondaryButtonClick();
    else navigate(-1);
  };

  useEffect(() => {
    dispatch(
      toggleFullSidebarWithCollapseButton({
        isOpen: false,
        showCollapseButton: false,
      }),
    );
    return () => {
      dispatch(toggleFullSidebar(true));
    };
  }, []);

  useEffect(() => {
    if (!siteCoreLoading) {
      setSectionHeaderTitles({
        participantInformation: fetchComponentTitle(
          createGenericAccounts.participantInformationTitle,
        ),
        programInformation: fetchComponentTitle(
          createGenericAccounts.programInformationTitle,
        ),
        platformDetails: fetchComponentTitle(
          createGenericAccounts.platformDetailsTitle,
        ),
        contactPreferences: fetchComponentTitle(
          createGenericAccounts.contactPreferencesTitle,
        ),
        upcomingMeetings: fetchLabel(
          createGenericAccounts.eventKey,
          createGenericAccounts.eventTitle,
        ),
        participantActivityTimeline: fetchComponentTitle(
          createGenericAccounts.participantActivityTimelineTitle,
        ),
      });
    }
  }, [siteCoreLoading]);

  return (
    <EditViewParticipantWrapper>
      <MuiButtonTertiary
        data-testid={`back-button`}
        onClick={handleSecondaryButtonClick}
      >
        <ArrowBackIosIcon />
        {commonLabels.back}
      </MuiButtonTertiary>
      <ViewGenericAccountHeader
        title={headerTitle}
        showEditButton={showEditButtonInHeader}
        onEditClick={onHeaderEditButtonClick}
      />
      <ExpandCollapseButton
        accordionStatus={accordionStatus}
        handleExpandCollapseAllChange={handleExpandCollapseAllChange}
      />

      <MuiBox>
        {Object.entries(participantData || {}).map(
          ([sectionKey, sectionData]) => (
            <MuiBox className="participant-details-container" key={sectionKey}>
              {sectionData.hasPermission && (
                <AccordionHeader
                  title={
                    <AccordionHeaderText
                      label={sectionHeaderTitles[sectionKey] || sectionKey}
                    />
                  }
                  onChange={() => handleIndividualAccordionChange(sectionKey)}
                  isExpanded={accordionStatus[sectionKey]}
                >
                  <MuiGrid
                    container
                    columnSpacing={2}
                    rowSpacing={5}
                    className="participant-section-grid"
                  >
                    {sectionData?.details?.map(
                      (item: {
                        label: string;
                        value?: string | string[] | ChipOptionsDataType[];
                        isEdit?: boolean;
                        showEllipsis?: boolean;
                        render?: () => JSX.Element;
                      }) =>
                        !Array.isArray(item.value) ? (
                          <MuiGrid size={gridSize} key={item.label}>
                            {item?.isEdit && item?.render ? (
                              item?.render()
                            ) : (
                              <MuiDoubleLabel
                                label={item.label}
                                replaceEmptyWithDash={isEmpty(item.value)}
                                isValueWithEllipsis={item.showEllipsis}
                                value={item.value}
                              />
                            )}
                          </MuiGrid>
                        ) : (
                          <MuiGrid size={gridSize} key={item.label}>
                            <MuiDoubleLabelWithChip
                              label={item.label}
                              replaceEmptyWithDash={isEmpty(item.value)}
                              value={item.value as ChipOptionsDataType[]}
                              chipPopoverGridRef={React.createRef<HTMLElement>()}
                              chipPopoverHeaderLabel={item.label}
                              showMoreARIALabel={commonARIALabel.showNMore}
                              removeChipNameARIALabel={
                                commonARIALabel.removeChipName
                              }
                              closePopoverARIALabel={
                                commonARIALabel.closePopover
                              }
                            />
                          </MuiGrid>
                        ),
                    )}
                  </MuiGrid>
                </AccordionHeader>
              )}
            </MuiBox>
          ),
        )}

        <PermissionWrapper
          permission={MODULE_PERMISSIONS.GENERIC_ACCOUNT_VIEW_EVENTS}
        >
          <MuiBox className="participant-details-container">
            <AccordionHeader
              wrapperClassName="participant-activity-timeline"
              title={
                <AccordionHeaderText
                  label={sectionHeaderTitles.upcomingMeetings}
                />
              }
              onChange={() =>
                handleIndividualAccordionChange("upcomingMeetings")
              }
              isExpanded={accordionStatus.upcomingMeetings}
            >
              <FormProvider {...upcomingEventMethods}>
                <ManageParticipantsSessionAndEventsTab
                  sessionsEventList={sessionsEventList}
                  showEventCategory={false}
                />
              </FormProvider>
            </AccordionHeader>
          </MuiBox>
        </PermissionWrapper>

        <PermissionWrapper
          permission={
            MODULE_PERMISSIONS.GENERIC_ACCOUNT_VIEW_PARTICIPANT_ACTIVITY_TIMELINE
          }
        >
          <MuiBox className="participant-details-container">
            <AccordionHeader
              wrapperClassName="participant-activity-timeline"
              title={
                <AccordionHeaderText
                  label={sectionHeaderTitles.participantActivityTimeline}
                />
              }
              onChange={() =>
                handleIndividualAccordionChange("participantActivityTimeline")
              }
              isExpanded={accordionStatus.participantActivityTimeline}
            >
              <CPParticipantActivityTimelineWrapper>
                <FormProvider {...participantActivityTimelineMethods}>
                  <ParticipantActivityTimeline
                    pageId={PAGE_IDS.VIEW_PARTICIPANT}
                    isEditProgram={isEditProgram}
                    programId={participantId}
                    displayAddBtn={isEditProgram}
                    showNextContactDate={false}
                    isFromGenericParticipant={true}
                  />
                </FormProvider>
              </CPParticipantActivityTimelineWrapper>
            </AccordionHeader>
          </MuiBox>
        </PermissionWrapper>
      </MuiBox>

      <MuiFooterButtons
        secondaryButtonLabel={secondaryButtonTitle}
        primaryButtonLabel={primaryButtonTitle}
        onSecondaryClick={handleSecondaryButtonClick}
        onPrimaryClick={onPrimaryButtonClick}
      />
    </EditViewParticipantWrapper>
  );
};
